import type { RootState } from '@/app/store';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Loader2, Volume2 } from 'lucide-react';
import {
  fetchPublicOffer,
  postAnalyticsEvent,
  fetchPublicResourceState,
} from '@/features/live/liveThunks';
import { clearPublicOffer, clearPublicSurvey, clearPublicQuiz } from '@/features/live/liveSlice';
import { applySharedResourceLiveEvent } from '@/features/webinar/webinarSlice';
import type { SharedResourcePublic } from '@/features/webinar/types';
import { parseYoutubeId } from '@/lib/youtube';
import { resourceStateRefetchDelayMs } from '@/lib/liveEventBatch';
import type { VideoHlsViewHandle } from '@/components/pages/live/attendee/VideoHlsView';

type YoutubePlayer = {
  getCurrentTime: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  destroy: () => void;
  unMute: () => void;
  /** Optionnels : selon la version de l'API chargée, tout le contrat n'est pas exposé. */
  isMuted?: () => boolean;
  getPlayerState?: () => number;
  playVideo: () => void;
};

type YoutubeApi = {
  Player: new (
    element: HTMLElement | string,
    options: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: { onReady?: () => void };
    },
  ) => YoutubePlayer;
};

declare global {
  interface Window {
    YT?: YoutubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

// Au-delà de ce bond de position entre deux ticks (250ms), on considère qu'il y a eu un saut en avant
// (clic « En direct » ou rattrapage auto YouTube), pas une lecture normale.
const SEEK_FORWARD_THRESHOLD_MS = 5000;

const BROADCAST_DELAY_MS = 7000;

// Délai laissé au lecteur pour confirmer l'activation du son : les commandes traversent l'iframe.
const UNMUTE_CONFIRM_DELAY_MS = 1000;

/** Code d'état « en pause » de l'API YouTube. */
const YOUTUBE_STATE_PAUSED = 2;

// Rattrapage du direct : au-delà de ce retard sur le live edge, on resaute près du direct.
// On vise le live edge moins une petite marge (CATCHUP_TARGET_MARGIN_S) pour ne pas rebufferiser
// à l'extrême pointe. Cooldown pour éviter de resauter à chaque tick.
const CATCHUP_BEHIND_THRESHOLD_S = 8;
const CATCHUP_TARGET_MARGIN_S = 2;
const CATCHUP_COOLDOWN_MS = 4000;

let iframeApiPromise: Promise<YoutubeApi> | null = null;

function loadYoutubeIframeApi(): Promise<YoutubeApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window unavailable'));
  }
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }
  if (iframeApiPromise) {
    return iframeApiPromise;
  }

  iframeApiPromise = new Promise<YoutubeApi>((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      if (window.YT) resolve(window.YT);
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_API_SRC}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.src = IFRAME_API_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return iframeApiPromise;
}

export type YouTubeWatchViewHandle = VideoHlsViewHandle;

export default forwardRef<
  YouTubeWatchViewHandle,
  {
    target: 'mobile' | 'desktop';
    youtubeUrl?: string | null;
    startedAtMs?: number | null;
    actualStartTimeMs?: number | null;
    onSeekToLive?: () => void;
  }
>(function YouTubeWatchView(
  { target, youtubeUrl, startedAtMs, actualStartTimeMs, onSeekToLive },
  ref,
) {
  const dispatch = useAppDispatch();
  const pendingResourceStateRefetchIds = useRef<Set<number>>(new Set());
  useEffect(
    () => () => {
      pendingResourceStateRefetchIds.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      pendingResourceStateRefetchIds.current.clear();
    },
    [],
  );

  // Refetch étalé : sur un même event, tous les spectateurs taperaient `/resources/state` en
  // simultané.
  const scheduleResourceStateRefetch = (
    targetWebinarId: number,
    targetSessionIdentifier: string,
    authorization: string,
  ) => {
    const timeoutId = window.setTimeout(() => {
      pendingResourceStateRefetchIds.current.delete(timeoutId);
      dispatch(
        fetchPublicResourceState({
          webinarId: targetWebinarId,
          sessionIdentifier: targetSessionIdentifier,
          authorization,
          coalesce: true,
        }),
      );
    }, resourceStateRefetchDelayMs());
    pendingResourceStateRefetchIds.current.add(timeoutId);
  };
  const { t } = useWebinarT('webinars');
  const webinarId = useAppSelector((s: RootState) => s.live.publicSession?.session?.webinarId);
  const { sessionIdentifier: sessionIdentifierParam } = useParams<{
    sessionIdentifier: string;
  }>();
  const sessionIdentifier = sessionIdentifierParam;

  const videoId = useMemo(() => parseYoutubeId(youtubeUrl), [youtubeUrl]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YoutubePlayer | null>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number | null>(null);
  const [muted, setMuted] = useState(true);
  // Les méthodes du lecteur n'existent qu'à partir de onReady : avant, unMute() est perdu.
  const [playerReady, setPlayerReady] = useState(false);
  const [unmuteRequested, setUnmuteRequested] = useState(false);
  const unmuteCheckRef = useRef<number | null>(null);
  const currentTimestampRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(startedAtMs ?? null);
  startedAtRef.current = startedAtMs ?? null;
  const actualStartTimeRef = useRef<number | null>(actualStartTimeMs ?? null);
  actualStartTimeRef.current = actualStartTimeMs ?? null;
  const onSeekToLiveRef = useRef<(() => void) | undefined>(onSeekToLive);
  onSeekToLiveRef.current = onSeekToLive;
  const prevAnchoredTsRef = useRef<number | null>(null);
  const lastCatchupAtRef = useRef<number>(0);
  const sessionStatus = useAppSelector((s: RootState) => s.live.publicSession?.session?.status);
  const backgroundColor = useAppSelector(
    (s: RootState) => s.live.publicSession?.visualIdentity?.backgroundColor,
  );
  const statusRef = useRef<string | undefined>(sessionStatus);
  statusRef.current = sessionStatus;

  const [timeBasedEvent, setTimeBasedEvent] = useState<
    {
      time: number | null;
      action: () => void;
      label: string;
    }[]
  >([]);

  useEffect(() => {
    if (!videoId || !containerRef.current) return;

    let destroyed = false;
    setPlayerReady(false);
    const mountPoint = document.createElement('div');
    containerRef.current.appendChild(mountPoint);

    loadYoutubeIframeApi()
      .then((api) => {
        if (destroyed) return;
        playerRef.current = new api.Player(mountPoint, {
          videoId,
          playerVars: {
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1,
            modestbranding: 1,
            // Spectateur verrouillé sur le direct : pas de barre de progression ni de raccourcis clavier,
            // donc pas de scrub possible. Le rattrapage du live edge est géré par le code.
            controls: 0,
            disablekb: 1,
            autoplay: 1,
            mute: 1,
          },
          events: {
            onReady: () => {
              if (!destroyed) setPlayerReady(true);
            },
          },
        });
      })
      .catch(() => {});

    return () => {
      destroyed = true;
      try {
        playerRef.current?.destroy();
      } catch {
        /* ignore */
      }
      playerRef.current = null;
      mountPoint.remove();
    };
  }, [videoId]);

  useEffect(() => {
    if (!videoId) return;
    const interval = window.setInterval(() => {
      let next: number | null;
      let anchored = false;
      if (statusRef.current === 'IN_PROGRESS') {
        // En live, on ne se cale plus sur l'horloge réelle (Date.now()) mais sur la position de lecture
        // du player YouTube, retardée par l'encodage StreamYard→YouTube. Ainsi une interaction déclenchée
        // à l'instant T ne s'affiche que lorsque la vidéo du spectateur atteint réellement T, et le retard
        // s'auto-ajuste à la latence propre à chaque spectateur. Sans actualStartTime (diffusion pas encore
        // en ligne / YouTube indispo), on retombe sur l'affichage immédiat.
        const player = playerRef.current;
        const anchor = actualStartTimeRef.current;
        const seconds = player?.getCurrentTime?.();
        if (anchor != null) {
          if (typeof seconds === 'number' && !Number.isNaN(seconds)) {
            // Plancher de sécurité : par à-coups (gigue du live YouTube), la position de lecture peut
            // repasser devant le live edge théorique et écraser le délai sous le retard d'encodage.
            // On garantit donc un délai minimum = BROADCAST_DELAY_MS (le spectateur ne peut pas, même
            // au plus près du direct, entendre le présentateur avant ce délai d'encodage StreamYard→YouTube).
            next = Math.min(
              anchor + seconds * 1000 - BROADCAST_DELAY_MS,
              Date.now() - BROADCAST_DELAY_MS,
            );
            anchored = true;

            // Rattrapage du direct : si la lecture a pris trop de retard sur le live edge
            // (position attendue = temps écoulé depuis la mise à l'antenne), on resaute près du direct.
            const liveEdgeSeconds = (Date.now() - anchor) / 1000;
            const behindSeconds = liveEdgeSeconds - seconds;
            const now = Date.now();
            if (
              behindSeconds > CATCHUP_BEHIND_THRESHOLD_S &&
              now - lastCatchupAtRef.current > CATCHUP_COOLDOWN_MS &&
              player
            ) {
              lastCatchupAtRef.current = now;
              try {
                player.seekTo(liveEdgeSeconds - CATCHUP_TARGET_MARGIN_S, true);
                player.playVideo();
              } catch {
                /* ignore */
              }
            }
          } else {
            // Player pas encore prêt (getCurrentTime indisponible au démarrage) : on garde les
            // interactions en attente plutôt que de les libérer tout de suite. Elles se déclencheront
            // au bon moment dès que la position de lecture est disponible.
            next = null;
          }
        } else {
          // Pas d'ancre (diffusion pas encore en ligne / YouTube indispo) : affichage immédiat, seul repli possible.
          next = Date.now();
        }
      } else {
        const player = playerRef.current;
        if (!player || typeof player.getCurrentTime !== 'function') return;
        const seconds = player.getCurrentTime();
        if (typeof seconds !== 'number' || Number.isNaN(seconds)) return;
        const anchor = startedAtRef.current;
        next = anchor != null ? anchor + seconds * 1000 : null;
      }

      // Saut en avant (clic « En direct » / rattrapage auto YouTube) : la position bondit. On purge les
      // events encore en attente (sinon ils se libèrent en rafale → l'offre clignote/saute) et on demande
      // au parent de resynchroniser l'affichage sur l'état réel des ressources renvoyé par le back.
      if (anchored && next != null) {
        const seekTarget = next;
        const previous = prevAnchoredTsRef.current;
        if (previous != null && seekTarget - previous > SEEK_FORWARD_THRESHOLD_MS) {
          setTimeBasedEvent((events) =>
            events.filter((event) => event.time != null && event.time > seekTarget),
          );
          onSeekToLiveRef.current?.();
        }
        prevAnchoredTsRef.current = seekTarget;
      } else {
        prevAnchoredTsRef.current = null;
      }

      currentTimestampRef.current = next;
      setCurrentTimestamp(next);
    }, 250);

    return () => window.clearInterval(interval);
  }, [videoId]);

  useEffect(() => {
    const actionsToRun = timeBasedEvent.filter(
      (event) => event.time && currentTimestamp && event.time < currentTimestamp,
    );
    actionsToRun.forEach((action) => action.action());
    setTimeBasedEvent((prev) => prev.filter((event) => !actionsToRun.includes(event)));
  }, [currentTimestamp]);

  const handleResourceEvent = useCallback(
    ({
      eventType,
      resourceType,
      resourceId,
      timeNow,
      authorization,
      resource,
    }: {
      eventType: 'RESOURCE_SHOW' | 'RESOURCE_HIDE';
      resourceType: string;
      resourceId: number;
      timeNow: number;
      authorization: string;
      resource?: SharedResourcePublic;
    }) => {
      if (
        (resourceType !== 'OFFER_TEMPLATE' &&
          resourceType !== 'SURVEY_TEMPLATE' &&
          resourceType !== 'QUIZ_TEMPLATE' &&
          resourceType !== 'SHARED_RESOURCE') ||
        !webinarId ||
        !authorization
      ) {
        return;
      }

      const runEventAction = () => {
        if (resourceType === 'SHARED_RESOURCE') {
          if (eventType === 'RESOURCE_SHOW' && resource) {
            dispatch(applySharedResourceLiveEvent({ type: 'SHARED_RESOURCE_SHOW', resource }));
          } else if (eventType === 'RESOURCE_HIDE') {
            dispatch(
              applySharedResourceLiveEvent({
                type: 'SHARED_RESOURCE_HIDE',
                resource: { id: resourceId },
              }),
            );
          }
          return;
        }

        if (eventType === 'RESOURCE_SHOW') {
          if (webinarId && sessionIdentifier && authorization) {
            scheduleResourceStateRefetch(webinarId, sessionIdentifier, authorization);
          }
          if (resourceType === 'OFFER_TEMPLATE') {
            dispatch(fetchPublicOffer({ webinarId, offerId: resourceId, authorization }));
            if (webinarId && sessionIdentifier && authorization) {
              dispatch(
                postAnalyticsEvent({
                  webinarId,
                  sessionIdentifier,
                  authorization,
                  input: {
                    eventType: 'OFFER_SEEN',
                    payloadJson: JSON.stringify({ offerId: resourceId }),
                    occurredAt: new Date().toISOString(),
                  },
                }),
              );
            }
          }
        } else if (eventType === 'RESOURCE_HIDE') {
          if (webinarId && sessionIdentifier && authorization) {
            scheduleResourceStateRefetch(webinarId, sessionIdentifier, authorization);
          }
          if (resourceType === 'OFFER_TEMPLATE') {
            dispatch(clearPublicOffer());
          } else if (resourceType === 'SURVEY_TEMPLATE') {
            dispatch(clearPublicSurvey());
          } else if (resourceType === 'QUIZ_TEMPLATE') {
            dispatch(clearPublicQuiz());
          }
        }
      };

      const label = `${eventType} ${resourceType} ${resourceId}`;
      setTimeBasedEvent((prev) => [...prev, { time: timeNow, action: runEventAction, label }]);
    },
    [dispatch, webinarId, sessionIdentifier],
  );

  const handleSurveyResultsEvent = useCallback(
    ({
      eventType,
      templateId,
      timeNow,
      onUpdate,
    }: {
      eventType: 'SHOW_SURVEY_RESPONSE' | 'HIDE_SURVEY_RESPONSE';
      templateId: number;
      timeNow: number;
      onUpdate: (templateId: number, show: boolean) => void;
    }) => {
      const runEventAction = () => {
        onUpdate(templateId, eventType === 'SHOW_SURVEY_RESPONSE');
      };
      const label = `${eventType} survey ${templateId}`;
      setTimeBasedEvent((prev) => [...prev, { time: timeNow, action: runEventAction, label }]);
    },
    [],
  );

  const handleEndSessionEvent = useCallback(
    ({ timeNow, onNavigate }: { timeNow: number; onNavigate: () => void }) => {
      setTimeBasedEvent((prev) => [
        ...prev,
        { time: timeNow, action: onNavigate, label: 'END_SESSION' },
      ]);
    },
    [],
  );

  // Le player démarre muet (autoplay navigateur). Le clic du spectateur (geste utilisateur requis
  // pour activer le son) déclenche unMute() puis masque l'overlay.
  /**
   * Envoie la commande si le lecteur peut la recevoir. Les appels partent en postMessage vers
   * l'iframe : l'état ne bascule pas dans la foulée, donc on masque l'overlay tout de suite et on
   * vérifie juste après. Si le son n'est pas passé, l'overlay revient et le spectateur peut
   * réessayer.
   */
  const requestUnmute = useCallback(() => {
    const player = playerRef.current;
    if (!player || typeof player.unMute !== 'function') return false;
    try {
      player.unMute();
      player.playVideo();
    } catch {
      return false;
    }
    setMuted(false);
    if (unmuteCheckRef.current != null) window.clearTimeout(unmuteCheckRef.current);
    unmuteCheckRef.current = window.setTimeout(() => {
      unmuteCheckRef.current = null;
      const currentPlayer = playerRef.current;
      // La commande est partie : on ne réaffiche l'overlay que si le lecteur affirme qu'elle n'a
      // pas abouti. Sans lecteur ou sans méthode de lecture d'état, on ne sait pas, et « je ne
      // sais pas » ne doit pas repasser un spectateur qui entend le son devant un écran bloquant.
      if (!currentPlayer) return;
      const stillMuted = currentPlayer.isMuted?.() === true;
      // Certains navigateurs rétablissent le son en mettant la vidéo en pause : le son seul ne
      // suffit donc pas à conclure au succès.
      const paused = currentPlayer.getPlayerState?.() === YOUTUBE_STATE_PAUSED;
      if (stillMuted || paused) setMuted(true);
    }, UNMUTE_CONFIRM_DELAY_MS);
    return true;
  }, []);

  const handleActivateSound = useCallback(() => {
    if (requestUnmute()) {
      setUnmuteRequested(false);
      return;
    }
    // Lecteur pas encore prêt : on retient l'intention et on garde l'overlay, sinon le clic est
    // perdu et le spectateur n'a plus aucun moyen d'activer le son.
    setUnmuteRequested(true);
  }, [requestUnmute]);

  useEffect(() => {
    if (!playerReady || !unmuteRequested) return;
    if (requestUnmute()) setUnmuteRequested(false);
  }, [playerReady, unmuteRequested, requestUnmute]);

  useEffect(
    () => () => {
      if (unmuteCheckRef.current != null) window.clearTimeout(unmuteCheckRef.current);
    },
    [],
  );

  useImperativeHandle(ref, () => ({
    target,
    handleResourceEvent,
    handleSurveyResultsEvent,
    handleEndSessionEvent,
    getCurrentTimestamp: () => currentTimestampRef.current,
  }));

  return (
    <div
      className="relative w-full max-h-full lg:h-full min-w-0 min-h-0 rounded-none lg:rounded-2xl overflow-hidden flex items-center justify-center compact-landscape:h-full compact-landscape:rounded-2xl"
      style={{
        // Même ratio fixe que le live classique : sans lui le conteneur s'effondre en mobile, où le
        // parent n'a de hauteur qu'à partir de lg, et YouTube letterboxe dans un cadre trop plat.
        aspectRatio: 16 / 9,
        backgroundColor: backgroundColor ?? '#ffffff',
      }}
    >
      {/* Contrôles natifs désactivés. L'overlay « activer le son » couvre la vidéo tant qu'elle est
          muette. Une fois le son activé, une couche transparente capte les clics pour empêcher
          pause/lecture (et donc la dérive volontaire) : le spectateur reste verrouillé sur le direct. */}
      {/* Cadre 16:9 centré : à partir de lg la colonne est plus haute que la vidéo et le ratio de la
          racine est ignoré (hauteur définie). Sans ce cadre l'iframe serait étirée et YouTube
          letterboxerait lui-même en noir au lieu de laisser voir la couleur de scène.
          La zone utile (w-full + max-h-full) peut être plus large que 16:9 quand la hauteur est
          rabotée : c'est l'élément interne, dimensionné par sa hauteur, qui porte le vrai ratio. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full max-h-full aspect-video flex items-center justify-center">
          <div className="relative h-full aspect-video overflow-hidden rounded-none lg:rounded-2xl">
            <div
              ref={containerRef}
              className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full"
            />
          </div>
        </div>
      </div>
      {!muted && <div className="absolute inset-0 z-10" aria-hidden="true" />}
      {muted && (
        <button
          type="button"
          onClick={handleActivateSound}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/60 text-white backdrop-blur-sm cursor-pointer"
        >
          {playerReady ? (
            <Volume2 className="size-10" />
          ) : (
            <Loader2 className="size-10 animate-spin" />
          )}
          <span className="text-lg md:text-2xl font-bold">
            {playerReady ? t('live.youtube.unmute.title') : t('live.youtube.unmute.preparing')}
          </span>
          <span className="max-w-md px-4 text-center text-xs text-white/80 md:text-sm">
            {t('live.youtube.unmute.subtitle')}
          </span>
        </button>
      )}
    </div>
  );
});
